import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Building2, LayoutGrid, Calendar, Youtube, Cpu,
  Check, Loader2, X, ExternalLink, AlertCircle, Copy, RefreshCw, Eye, EyeOff, Download, KeyRound,
  ListChecks, CheckCircle2, Circle, Lock, Bell, Camera,
} from 'lucide-react';
import { CameraInventoryTab } from '@/components/facility/CameraInventoryTab';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { facilitiesApi, dashboardApi, youtubeApi, agentHostApi, schedulesApi, obsTemplateApi, setupApi, queryKeys, UpdateFacilityRequest, AgentSessionMapping, AgentHost, ControlHubConfig, ControlHubTab, SetupStep } from '@/lib/api';
import { useAuthStore } from '@/store/auth';

// ─── Common IANA timezones ─────────────────────────────────────────────────────
const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Toronto',
  'America/Vancouver',
  'America/Edmonton',
  'America/Winnipeg',
  'America/Halifax',
  'America/St_Johns',
  'Europe/London',
  'Europe/Dublin',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Stockholm',
  'Europe/Helsinki',
  'Europe/Zurich',
  'Europe/Amsterdam',
  'Europe/Oslo',
  'Europe/Copenhagen',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Perth',
  'Pacific/Auckland',
  'UTC',
];

// ─── Shared field styles ───────────────────────────────────────────────────────
const inputCls =
  'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';
const labelCls = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

// ─── Tab definitions ───────────────────────────────────────────────────────────
type Tab = 'setup' | 'general' | 'sheets' | 'schedule' | 'youtube' | 'agent' | 'cameras' | 'alerts';

// ─── SaveBar ──────────────────────────────────────────────────────────────────
function SaveBar({
  onSave,
  isPending,
  saved,
  error,
}: {
  onSave: () => void;
  isPending: boolean;
  saved: boolean;
  error: string | null;
}) {
  return (
    <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
      <Button onClick={onSave} disabled={isPending} size="sm">
        {isPending ? (
          <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
        ) : saved ? (
          <Check className="mr-2 h-3.5 w-3.5 text-green-500" />
        ) : null}
        {isPending ? 'Saving…' : saved ? 'Saved' : 'Save Changes'}
      </Button>
      {error && (
        <span className="flex items-center gap-1 text-xs text-red-500">
          <AlertCircle className="h-3.5 w-3.5" />
          {error}
        </span>
      )}
    </div>
  );
}

// ─── General Tab ──────────────────────────────────────────────────────────────
function GeneralTab({
  facility,
  onSaved,
}: {
  facility: NonNullable<ReturnType<typeof useQuery>['data']>;
  onSaved: () => void;
}) {
  const f = facility as any;
  const [name, setName] = useState(f.name ?? '');
  const [isActive, setIsActive] = useState<boolean>(f.isActive ?? true);
  const [timezone, setTimezone] = useState(f.timezone ?? 'America/Chicago');
  const [maxLogos, setMaxLogos] = useState(String(f.maxLogos ?? 20));
  const [requiresTransparency, setRequiresTransparency] = useState<boolean>(
    f.requiresTransparency ?? true
  );
  const [notes, setNotes] = useState(f.notes ?? '');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (data: UpdateFacilityRequest) => facilitiesApi.update(f.id, data),
    onSuccess: () => {
      onSaved();
      setSaved(true);
      setError(null);
      setTimeout(() => setSaved(false), 3000);
    },
    onError: (err: any) => setError(err?.message || 'Failed to save'),
  });

  const handleSave = () => {
    if (!name.trim()) { setError('Name is required'); return; }
    mutation.mutate({
      name: name.trim(),
      isActive,
      timezone,
      maxLogos: parseInt(maxLogos) || 20,
      requiresTransparency,
      notes: notes || undefined,
    });
  };

  return (
    <div className="space-y-5">
      {/* Name */}
      <div>
        <label className={labelCls}>Facility Name</label>
        <input className={inputCls} value={name} onChange={e => setName(e.target.value)} />
      </div>

      {/* Active */}
      <div className="flex items-center gap-3">
        <input
          id="gen-active"
          type="checkbox"
          checked={isActive}
          onChange={e => setIsActive(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-blue-600"
        />
        <label htmlFor="gen-active" className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Active (facility can log in and use the system)
        </label>
      </div>

      {/* Timezone */}
      <div>
        <label className={labelCls}>Timezone</label>
        <select
          className={inputCls}
          value={timezone}
          onChange={e => setTimezone(e.target.value)}
        >
          {TIMEZONES.map(tz => (
            <option key={tz} value={tz}>{tz}</option>
          ))}
        </select>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Used for schedule display and stream scheduling.
        </p>
      </div>

      {/* Max Logos */}
      <div>
        <label className={labelCls}>Max Logos</label>
        <input
          className={inputCls}
          type="number"
          min={1}
          max={100}
          value={maxLogos}
          onChange={e => setMaxLogos(e.target.value)}
        />
      </div>

      {/* Requires Transparency */}
      <div className="flex items-center gap-3">
        <input
          id="gen-transparency"
          type="checkbox"
          checked={requiresTransparency}
          onChange={e => setRequiresTransparency(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-blue-600"
        />
        <label htmlFor="gen-transparency" className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Require transparent PNG uploads
        </label>
      </div>

      {/* Notes */}
      <div>
        <label className={labelCls}>Notes</label>
        <textarea
          className={inputCls}
          rows={3}
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Internal notes about this facility…"
        />
      </div>

      <SaveBar onSave={handleSave} isPending={mutation.isPending} saved={saved} error={error} />
    </div>
  );
}

// ─── Sheets Tab ───────────────────────────────────────────────────────────────
function SheetsTab({
  facility,
  onSaved,
}: {
  facility: any;
  onSaved: () => void;
}) {
  const parseJson = (raw: string): string[] => {
    try { return JSON.parse(raw); } catch { return []; }
  };

  const [sheetCount, setSheetCount] = useState(facility.sheetCount ?? 4);
  const [sheetNames, setSheetNames] = useState<string[]>(parseJson(facility.sheetNames ?? '[]'));
  const [pausedSheets, setPausedSheets] = useState<string[]>(parseJson(facility.pausedSheets ?? '[]'));
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep sheetNames length in sync with sheetCount
  useEffect(() => {
    setSheetNames(prev => {
      const next = [...prev];
      while (next.length < sheetCount) next.push('');
      return next.slice(0, sheetCount);
    });
  }, [sheetCount]);

  const autoPopulate = (style: 'alpha' | 'numeric') => {
    const names: string[] = [];
    for (let i = 0; i < sheetCount; i++) {
      names.push(style === 'alpha' ? String.fromCharCode(65 + i) : String(i + 1));
    }
    setSheetNames(names);
  };

  const mutation = useMutation({
    mutationFn: (data: UpdateFacilityRequest) => facilitiesApi.update(facility.id, data),
    onSuccess: () => {
      onSaved();
      setSaved(true);
      setError(null);
      setTimeout(() => setSaved(false), 3000);
    },
    onError: (err: any) => setError(err?.message || 'Failed to save'),
  });

  const togglePaused = (name: string) => {
    setPausedSheets(prev =>
      prev.includes(name) ? prev.filter(s => s !== name) : [...prev, name]
    );
  };

  const handleSave = () => {
    mutation.mutate({
      sheetCount,
      sheetNames: JSON.stringify(sheetNames),
      pausedSheets: JSON.stringify(pausedSheets),
    });
  };

  return (
    <div className="space-y-5">
      {/* Sheet Count */}
      <div>
        <label className={labelCls}>Number of Sheets</label>
        <input
          className={inputCls}
          type="number"
          min={1}
          max={20}
          value={sheetCount}
          onChange={e => setSheetCount(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
        />
      </div>

      {/* Sheet Names */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className={labelCls + ' mb-0'}>Sheet Names</label>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => autoPopulate('alpha')}>
              A, B, C…
            </Button>
            <Button variant="outline" size="sm" onClick={() => autoPopulate('numeric')}>
              1, 2, 3…
            </Button>
          </div>
        </div>
        <div className="space-y-2">
          {Array.from({ length: sheetCount }).map((_, i) => {
            const name = sheetNames[i] ?? '';
            const isPaused = name ? pausedSheets.includes(name) : false;
            return (
              <div key={i} className="flex items-center gap-2">
                <span className="w-16 text-xs text-gray-500 dark:text-gray-400">Sheet {i + 1}</span>
                <input
                  className={inputCls + ' flex-1'}
                  value={name}
                  onChange={e => {
                    const next = [...sheetNames];
                    next[i] = e.target.value;
                    setSheetNames(next);
                  }}
                  placeholder={`e.g. ${String.fromCharCode(65 + i)}`}
                />
                {name && (
                  <button
                    type="button"
                    onClick={() => togglePaused(name)}
                    title={isPaused ? 'Sheet suspended — click to restore' : 'Sheet active — click to suspend'}
                    className={`shrink-0 rounded-md px-2.5 py-1.5 text-xs font-medium border transition-colors ${
                      isPaused
                        ? 'bg-amber-100 border-amber-300 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/40 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/60'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700'
                    }`}
                  >
                    {isPaused ? '⏸ Suspended' : 'Active'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Sheet names are used in overlay displays and schedule exports.
          Suspended sheets are excluded from agent auto-start.
        </p>
      </div>

      <SaveBar onSave={handleSave} isPending={mutation.isPending} saved={saved} error={error} />
    </div>
  );
}

// ─── Schedule Tab ─────────────────────────────────────────────────────────────
function ScheduleTab({
  facility,
  onSaved,
}: {
  facility: any;
  onSaved: () => void;
}) {
  const [source, setSource] = useState<'manual' | 'google_sheets' | 'ccm_api' | 'leagueapps_api' | 'generic_recurring' | 'curlops_api'>(
    facility.scheduleSource ?? 'manual'
  );
  const [googleSheetsUrl, setGoogleSheetsUrl] = useState(facility.googleSheetsUrl ?? '');
  const [ccmApiUrl, setCcmApiUrl] = useState(facility.ccmApiUrl ?? '');
  const [ccmApiKey, setCcmApiKey] = useState(facility.ccmApiKey ?? '');
  const [laApiKey, setLaApiKey] = useState(facility.leagueAppsApiKey ?? '');
  const [laSiteId, setLaSiteId] = useState<string>(facility.leagueAppsSiteId ? String(facility.leagueAppsSiteId) : '');
  const [laLocationId, setLaLocationId] = useState<string>(facility.leagueAppsLocationId ? String(facility.leagueAppsLocationId) : '');
  const [laSourceTz, setLaSourceTz] = useState<string>(facility.leagueAppsSourceTz ?? '');
  const [curlOpsSlug, setCurlOpsSlug] = useState(facility.curlOpsSlug ?? '');
  const [curlOpsApiKey, setCurlOpsApiKey] = useState(facility.curlOpsApiKey ?? '');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (data: UpdateFacilityRequest) => facilitiesApi.update(facility.id, data),
    onSuccess: () => {
      onSaved();
      setSaved(true);
      setError(null);
      setTimeout(() => setSaved(false), 3000);
    },
    onError: (err: any) => setError(err?.message || 'Failed to save'),
  });

  const handleSave = () => {
    if (source === 'google_sheets' && !googleSheetsUrl.trim()) {
      setError('Google Sheets URL is required');
      return;
    }
    if (source === 'ccm_api' && !ccmApiUrl.trim()) {
      setError('CCM API URL is required');
      return;
    }
    if (source === 'leagueapps_api') {
      if (!laApiKey.trim()) { setError('LeagueApps API key is required'); return; }
      if (!laSiteId.trim() || !/^\d+$/.test(laSiteId.trim())) {
        setError('LeagueApps Site ID must be numeric'); return;
      }
      if (laLocationId.trim() && !/^\d+$/.test(laLocationId.trim())) {
        setError('LeagueApps Location ID must be numeric (or leave blank)'); return;
      }
    }
    if (source === 'curlops_api') {
      if (!curlOpsSlug.trim()) { setError('CurlOps facility slug is required'); return; }
      if (!curlOpsApiKey.trim()) { setError('CurlOps API key is required'); return; }
    }
    mutation.mutate({
      scheduleSource: source,
      googleSheetsUrl: source === 'google_sheets' ? googleSheetsUrl.trim() : null,
      ccmApiUrl: source === 'ccm_api' ? ccmApiUrl.trim() : null,
      ccmApiKey: source === 'ccm_api' ? (ccmApiKey.trim() || null) : null,
      leagueAppsApiKey: source === 'leagueapps_api' ? (laApiKey.trim() || null) : null,
      leagueAppsSiteId: source === 'leagueapps_api' ? parseInt(laSiteId.trim(), 10) : null,
      leagueAppsLocationId: source === 'leagueapps_api' && laLocationId.trim()
        ? parseInt(laLocationId.trim(), 10) : null,
      leagueAppsSourceTz: source === 'leagueapps_api' ? (laSourceTz.trim() || null) : null,
      curlOpsSlug: source === 'curlops_api' ? curlOpsSlug.trim() : null,
      curlOpsApiKey: source === 'curlops_api' ? (curlOpsApiKey.trim() || null) : null,
    });
  };

  return (
    <div className="space-y-5">
      {/* Source selector */}
      <div>
        <label className={labelCls}>Schedule Source</label>
        <div className="space-y-2">
          {[
            { value: 'manual', label: 'Manual Entry', description: 'Enter games manually in the Schedules page.' },
            { value: 'google_sheets', label: 'Google Sheets', description: 'Pull schedule from a public Google Sheets document.' },
            { value: 'ccm_api', label: 'CurlingClubManager API', description: 'Sync schedule automatically from curlingclubmanager.com.' },
            { value: 'leagueapps_api', label: 'LeagueApps API', description: 'Sync schedule automatically from a LeagueApps-powered club site.' },
            { value: 'generic_recurring', label: 'Generic Recurring', description: 'Define recurring weekly time slots (Day · Start · Stop · optional League name). Expanded 30 days ahead.' },
            { value: 'curlops_api', label: 'CurlOps', description: 'Sync schedule automatically from a CurlOps facility feed.' },
          ].map(opt => (
            <label
              key={opt.value}
              className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                source === opt.value
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <input
                type="radio"
                name="scheduleSource"
                value={opt.value}
                checked={source === opt.value}
                onChange={() => setSource(opt.value as any)}
                className="mt-0.5 h-4 w-4 text-blue-600"
              />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{opt.label}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{opt.description}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Google Sheets fields */}
      {source === 'google_sheets' && (
        <div>
          <label className={labelCls}>Public Google Sheets URL</label>
          <input
            className={inputCls}
            type="url"
            value={googleSheetsUrl}
            onChange={e => setGoogleSheetsUrl(e.target.value)}
            placeholder="https://docs.google.com/spreadsheets/d/…"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            The sheet must be published to the web (File → Share → Publish to web).
          </p>
        </div>
      )}

      {/* CCM API fields */}
      {source === 'ccm_api' && (
        <div className="space-y-4">
          <div>
            <label className={labelCls}>CCM API Base URL</label>
            <input
              className={inputCls}
              type="url"
              value={ccmApiUrl}
              onChange={e => setCcmApiUrl(e.target.value)}
              placeholder="https://curlingclubmanager.com/api/…"
            />
          </div>
          <div>
            <label className={labelCls}>CCM API Key</label>
            <input
              className={inputCls}
              type="password"
              value={ccmApiKey}
              onChange={e => setCcmApiKey(e.target.value)}
              placeholder="Your CCM API key"
              autoComplete="off"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Optional — only required if your CCM instance uses API key authentication.
            </p>
          </div>
        </div>
      )}

      {/* Generic recurring: enabling the source is done here; the weekly slots
          themselves are configured on the facility's Schedules page (single source
          of truth, shared with club managers). */}
      {source === 'generic_recurring' && (
        <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-4 text-sm text-blue-800 dark:border-blue-900/50 dark:bg-blue-950/20 dark:text-blue-300">
          Configure recurring schedules on the <span className="font-semibold">Schedules page</span> for this facility.
        </div>
      )}

      {/* LeagueApps fields */}
      {source === 'leagueapps_api' && (
        <div className="space-y-4">
          <div>
            <label className={labelCls}>LeagueApps API Key</label>
            <input
              className={inputCls}
              type="password"
              value={laApiKey}
              onChange={e => setLaApiKey(e.target.value)}
              placeholder="Issued by LeagueApps (sent as ?la-api-key=…)"
              autoComplete="off"
            />
          </div>
          <div>
            <label className={labelCls}>LeagueApps Site ID</label>
            <input
              className={inputCls}
              type="text"
              inputMode="numeric"
              value={laSiteId}
              onChange={e => setLaSiteId(e.target.value)}
              placeholder="e.g. 14988"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Found via <code className="rounded bg-gray-100 px-1 py-0.5 dark:bg-gray-800">https://&lt;subdomain&gt;.leagueapps.com/api/sites/subdomains/&lt;subdomain&gt;</code>.
            </p>
          </div>
          <div>
            <label className={labelCls}>Location ID (optional)</label>
            <input
              className={inputCls}
              type="text"
              inputMode="numeric"
              value={laLocationId}
              onChange={e => setLaLocationId(e.target.value)}
              placeholder="e.g. 26958 — leave blank to include all locations at the site"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Filters games to a single physical venue. Useful for multi-rink LeagueApps sites.
            </p>
          </div>
          <div>
            <label className={labelCls}>Feed reports times as (optional)</label>
            <select
              className={inputCls}
              value={laSourceTz}
              onChange={e => setLaSourceTz(e.target.value)}
            >
              <option value="">Trust the feed (normal)</option>
              <option value="America/New_York">Eastern — America/New_York</option>
              <option value="America/Chicago">Central — America/Chicago</option>
              <option value="America/Denver">Mountain — America/Denver</option>
              <option value="America/Los_Angeles">Pacific — America/Los_Angeles</option>
            </select>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Only set this if LeagueApps sends the wrong zone. Dakota’s feed reports Eastern
              although their LeagueApps console is set to Central, which put every game an hour
              early. Setting this re-reads each start time in the zone chosen here and keeps the
              same wall clock in this facility’s own timezone. Leave on “Trust the feed” unless
              you have confirmed a mismatch — clearing it undoes the correction.
            </p>
          </div>
        </div>
      )}

      {/* CurlOps fields */}
      {source === 'curlops_api' && (
        <div className="space-y-4">
          <div>
            <label className={labelCls}>CurlOps Facility Slug</label>
            <input
              className={inputCls}
              type="text"
              value={curlOpsSlug}
              onChange={e => setCurlOpsSlug(e.target.value)}
              placeholder="e.g. shout-your-name"
              autoComplete="off"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              The facility slug from CurlOps (appears in the feed URL <code className="rounded bg-gray-100 px-1 py-0.5 dark:bg-gray-800">/facilities/&lt;slug&gt;/games</code>).
            </p>
          </div>
          <div>
            <label className={labelCls}>CurlOps API Key</label>
            <input
              className={inputCls}
              type="password"
              value={curlOpsApiKey}
              onChange={e => setCurlOpsApiKey(e.target.value)}
              placeholder="Read-only feed API key (sent as X-API-Key)"
              autoComplete="off"
            />
          </div>
        </div>
      )}

      <SaveBar onSave={handleSave} isPending={mutation.isPending} saved={saved} error={error} />

      {/* ── Upcoming schedule preview ── */}
      <UpcomingSchedulesPreview facilityId={facility.facilityId} timezone={facility.timezone} />
    </div>
  );
}

// ─── Upcoming Schedules Preview ───────────────────────────────────────────────
function UpcomingSchedulesPreview({ facilityId, timezone }: { facilityId: string; timezone?: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['schedule-preview', facilityId],
    queryFn: () => schedulesApi.list({ facilityId, status: 'pending' }),
    staleTime: 60_000,
  });

  const schedules = data?.schedules ?? [];

  function fmtDt(iso: string) {
    try {
      return new Date(iso).toLocaleString([], {
        month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
        timeZone: timezone,
      });
    } catch { return iso; }
  }

  return (
    <div className="pt-2">
      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        Upcoming Schedules
        {schedules.length > 0 && (
          <span className="ml-2 text-xs font-normal text-green-600 dark:text-green-400">
            ✓ {schedules.length} found
          </span>
        )}
      </h3>
      {isLoading ? (
        <p className="text-xs text-gray-400">Loading…</p>
      ) : schedules.length === 0 ? (
        <p className="text-xs text-gray-400 italic">No upcoming pending schedules found.</p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Sheet</th>
                <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Event</th>
                <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Start</th>
                <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">End</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
              {schedules.slice(0, 10).map(s => (
                <tr key={s.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                  <td className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300">{s.sheetId}</td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400 max-w-[180px] truncate">{s.eventName}</td>
                  <td className="px-3 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap">{fmtDt(s.scheduledStart)}</td>
                  <td className="px-3 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap">{fmtDt(s.scheduledEnd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {schedules.length > 10 && (
            <p className="px-3 py-2 text-xs text-gray-400 border-t border-gray-100 dark:border-gray-800">
              …and {schedules.length - 10} more
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── YouTube Tab (inline, no separate component needed) ───────────────────────
// ── Alerts tab: the site's own Discord channel ──────────────────────────────
// Copies of this site's alerts (stream overdue, sheet import problems, schedule
// issues new/cleared) go to the channel set here, as well as the main alerts
// channel. The webhook URL is a secret: it is never shown again after saving.
function AlertsTab({ facility }: { facility: any }) {
  const facilityId: string = facility.facilityId;
  const queryClient = useQueryClient();
  const [url, setUrl] = useState('');
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const key = ['alert-channel', facilityId];
  const { data, isLoading } = useQuery({ queryKey: key, queryFn: () => dashboardApi.getAlertChannel(facilityId) });
  const done = (text: string) => { setNote({ ok: true, text }); queryClient.invalidateQueries({ queryKey: key }); };
  const fail = (e: unknown) => setNote({ ok: false, text: e instanceof Error ? e.message : 'Something went wrong' });
  const save = useMutation({ mutationFn: () => dashboardApi.setAlertChannel(facilityId, url.trim()),
    onSuccess: () => { setUrl(''); done('Saved. Send a test message to check it lands.'); }, onError: fail });
  const clear = useMutation({ mutationFn: () => dashboardApi.clearAlertChannel(facilityId),
    onSuccess: () => done('Removed. Alerts go to the main channel only.'), onError: fail });
  const test = useMutation({ mutationFn: () => dashboardApi.testAlertChannel(facilityId),
    onSuccess: () => setNote({ ok: true, text: 'Test message sent — check the channel.' }), onError: fail });

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Site Discord channel</h3>
        <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
          Copies of this site’s <span className="font-medium">stream-overdue alerts</span>,{' '}
          <span className="font-medium">sheet-import problems</span> and{' '}
          <span className="font-medium">schedule issues</span> (new and cleared) go to this channel as well as the
          main alerts channel. Agent, OBS and NDI faults stay in the main channel only. In Discord: channel settings →
          Integrations → Webhooks → New Webhook → Copy Webhook URL.
        </p>
      </div>

      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
      ) : (
        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-200">
          {data?.configured
            ? <>Channel set <span className="font-mono text-xs text-gray-500 dark:text-gray-400">({data.preview})</span></>
            : 'No site channel — alerts go to the main channel only.'}
        </div>
      )}

      <div className="space-y-2">
        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
          {data?.configured ? 'Replace webhook URL' : 'Discord webhook URL'}
        </label>
        <input
          type="password"
          autoComplete="off"
          value={url}
          onChange={e => { setUrl(e.target.value); setNote(null); }}
          placeholder="https://discord.com/api/webhooks/…"
          className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 font-mono text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        />
        <div className="flex flex-wrap gap-2">
          <Button size="sm" disabled={!url.trim() || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}Save
          </Button>
          {data?.configured && (
            <>
              <Button size="sm" variant="outline" disabled={test.isPending} onClick={() => test.mutate()}>
                {test.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}Send test message
              </Button>
              <Button size="sm" variant="outline" disabled={clear.isPending}
                onClick={() => { if (confirm('Stop sending this site’s alerts to its own channel?')) clear.mutate(); }}
                className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20">
                Remove
              </Button>
            </>
          )}
        </div>
        {note && (
          <p className={`text-xs ${note.ok ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{note.text}</p>
        )}
      </div>
    </div>
  );
}

function YouTubeTab({ facility }: { facility: any }) {
  const queryClient = useQueryClient();
  const facilityId = facility.facilityId as string;
  const [titleTemplate, setTitleTemplate] = useState('');
  const [templateSaved, setTemplateSaved] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);

  const { data: credData, isLoading: credLoading, refetch: refetchCreds } = useQuery({
    queryKey: queryKeys.youtube.credentials(facilityId),
    queryFn: () => youtubeApi.getCredentials(facilityId),
  });

  const { data: ruleData, isLoading: ruleLoading } = useQuery({
    queryKey: queryKeys.youtube.rule(facilityId),
    queryFn: () => youtubeApi.getRenameRule(facilityId),
  });

  useEffect(() => {
    if (ruleData?.rule) setTitleTemplate(ruleData.rule.titleTemplate || '');
  }, [ruleData]);

  const disconnectMutation = useMutation({
    mutationFn: () => youtubeApi.deleteCredentials(facilityId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.youtube.credentials(facilityId) }),
  });

  const saveTemplateMutation = useMutation({
    mutationFn: () => {
      if (ruleData?.rule) return youtubeApi.updateRenameRule(facilityId, { titleTemplate });
      return youtubeApi.saveRenameRule(facilityId, titleTemplate);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.youtube.rule(facilityId) });
      setTemplateSaved(true);
      setTemplateError(null);
      setTimeout(() => setTemplateSaved(false), 3000);
    },
    onError: (err: any) => setTemplateError(err?.message || 'Failed to save'),
  });

  const handleConnectYouTube = () => {
    const authUrl = `/api/youtube/oauth/authorize?facilityId=${facilityId}`;
    const popup = window.open(authUrl, 'youtube-auth', 'width=600,height=700,scrollbars=yes,resizable=yes');
    const listener = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'youtube-oauth-success') {
        window.removeEventListener('message', listener);
        popup?.close();
        queryClient.invalidateQueries({ queryKey: queryKeys.youtube.credentials(facilityId) });
      }
    };
    window.addEventListener('message', listener);
    const interval = setInterval(() => {
      if (popup?.closed) {
        clearInterval(interval);
        window.removeEventListener('message', listener);
        refetchCreds();
      }
    }, 1000);
  };

  const isConnected = credData?.connected ?? false;

  return (
    <div className="space-y-6">
      {/* Connection status */}
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Connection Status
        </p>
        {credLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
        ) : isConnected ? (
          <div className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-800 dark:bg-green-900/20">
            <div className="flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/40">
                <Check className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
              </span>
              <div>
                <p className="text-sm font-medium text-green-700 dark:text-green-400">Connected</p>
                {credData?.channelName && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">Channel: {credData.channelName}</p>
                )}
                {credData?.tokenExpiresAt && (
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    Token expires: {new Date(credData.tokenExpiresAt).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { if (confirm('Disconnect YouTube for this facility?')) disconnectMutation.mutate(); }}
              disabled={disconnectMutation.isPending}
              className="text-red-600 hover:border-red-300 hover:bg-red-50 dark:text-red-400"
            >
              <X className="mr-1 h-3.5 w-3.5" />
              Disconnect
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3 dark:border-gray-700">
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Youtube className="h-4 w-4" />
              Not connected
            </div>
            <Button size="sm" onClick={handleConnectYouTube}>
              <ExternalLink className="mr-1 h-3.5 w-3.5" />
              Connect YouTube
            </Button>
          </div>
        )}
      </div>

      {/* Title template */}
      <div>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Stream Title Template
        </p>
        {ruleLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
        ) : (
          <div className="space-y-2">
            <div className="flex gap-2">
              <input
                type="text"
                value={titleTemplate}
                onChange={e => setTitleTemplate(e.target.value)}
                placeholder="{team1} vs {team2} - Sheet {sheet}"
                className={inputCls}
              />
              <Button
                size="sm"
                onClick={() => {
                  if (!titleTemplate.trim()) { setTemplateError('Template cannot be empty'); return; }
                  saveTemplateMutation.mutate();
                }}
                disabled={saveTemplateMutation.isPending}
              >
                {saveTemplateMutation.isPending
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : templateSaved
                  ? <Check className="h-3.5 w-3.5 text-green-500" />
                  : 'Save'}
              </Button>
            </div>
            {templateError && <p className="text-xs text-red-500">{templateError}</p>}
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Variables:{' '}
              {['{team1}', '{team2}', '{sheet}', '{event}', '{date}'].map(v => (
                <code key={v} className="mr-1 rounded bg-gray-100 px-1 dark:bg-gray-800">{v}</code>
              ))}
            </p>
          </div>
        )}
      </div>

      {/* Stream keys */}
      <StreamKeysSection facilityId={facilityId} facilityName={facility.name} isConnected={isConnected} />
    </div>
  );
}

// Greyed out unless connected; if keys already exist the button locks behind a
// confirmation checkbox and warns on press to prevent accidental overwrites/dupes.
function StreamKeysSection({ facilityId, facilityName, isConnected }: { facilityId: string; facilityName: string; isConnected: boolean }) {
  const queryClient = useQueryClient();
  const [unlocked, setUnlocked] = useState(false);
  const [resultMsg, setResultMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { data: status, isLoading } = useQuery({
    queryKey: queryKeys.youtube.streamKeys(facilityId),
    queryFn: () => youtubeApi.getStreamKeyStatus(facilityId),
    enabled: isConnected,
  });

  const createMutation = useMutation({
    mutationFn: () => youtubeApi.createStreamKeys(facilityId),
    onSuccess: (res) => {
      setResultMsg(`Done — created ${res.created}, found ${res.found}${res.missing ? `, ${res.missing} still missing` : ''}.`);
      setErrorMsg(null);
      setUnlocked(false);
      queryClient.invalidateQueries({ queryKey: queryKeys.youtube.streamKeys(facilityId) });
      setTimeout(() => setResultMsg(null), 6000);
    },
    onError: (err: any) => setErrorMsg(err?.message || 'Failed to create stream keys'),
  });

  const withKey = status?.withKey ?? 0;
  const total = status?.total ?? 0;
  const keysExist = withKey > 0;
  const buttonDisabled = !isConnected || createMutation.isPending || (keysExist && !unlocked);

  const handleCreate = () => {
    if (!isConnected) return;
    const msg = keysExist
      ? `WARNING: ${facilityName} already has ${withKey}/${total} stream keys.\n\nRe-running creates one reusable stream per sheet whose stream isn't matched by title — which can produce DUPLICATE streams on the YouTube channel. Continue?`
      : `Create stream keys for ${facilityName}? This creates one reusable 1080p60 stream per sheet on the connected YouTube channel.`;
    if (!window.confirm(msg)) return;
    createMutation.mutate();
  };

  return (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Stream Keys</p>
      {!isConnected ? (
        <p className="text-xs text-gray-400 dark:text-gray-500">Connect a YouTube channel to create stream keys.</p>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {isLoading ? 'Checking…'
                : total === 0 ? 'No sheets configured'
                : keysExist ? `${withKey}/${total} sheets have keys`
                : `No keys yet (${total} sheets)`}
            </p>
            <Button size="sm" onClick={handleCreate} disabled={buttonDisabled}>
              {createMutation.isPending
                ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                : <KeyRound className="mr-1 h-3.5 w-3.5" />}
              Create Keys
            </Button>
          </div>
          {keysExist && (
            <label className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <input
                type="checkbox"
                checked={unlocked}
                onChange={(e) => setUnlocked(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-gray-300 dark:border-gray-600"
              />
              Keys already exist — tick to allow re-running (may create duplicates)
            </label>
          )}
          {resultMsg && <p className="text-xs text-green-600 dark:text-green-400">{resultMsg}</p>}
          {errorMsg && <p className="text-xs text-red-500">{errorMsg}</p>}
        </div>
      )}
    </div>
  );
}

// ─── HostCard — one editable card per agent host ─────────────────────────────
function HostCard({
  host,
  facilityId,
  sheetNames,
  allHosts,
}: {
  host: AgentHost;
  facilityId: string;
  sheetNames: string[];
  allHosts: AgentHost[];
}) {
  const queryClient = useQueryClient();

  const [obsUrl, setObsUrl]             = useState(host.obsWebSocketUrl ?? 'ws://localhost');
  const [obsPassword, setObsPassword]   = useState(host.obsWebSocketPassword ?? '');
  const [pollInterval, setPollInterval] = useState(String(host.pollIntervalSeconds ?? 60));
  const [hostLabel, setHostLabel]       = useState(host.hostLabel ?? 'Host 1');
  const [isActive, setIsActive]         = useState(host.isActive ?? true);
  const [notes, setNotes]               = useState(host.notes ?? '');
  const [macAddress, setMacAddress]     = useState(host.macAddress ?? '');
  const [panelUrl, setPanelUrl]         = useState(host.panelUrl ?? '');
  const [mappings, setMappings]         = useState<AgentSessionMapping[]>(host.sessionMappings ?? []);
  const [showPassword, setShowPassword] = useState(false);
  const [showApiKey, setShowApiKey]     = useState(false);
  const [showStreamKeys, setShowStreamKeys] = useState<Record<number, boolean>>({});
  const [copied, setCopied]             = useState(false);
  const [saved, setSaved]               = useState(false);
  const [saveError, setSaveError]       = useState<string | null>(null);
  const [downloading, setDownloading]   = useState(false);

  const handleDownloadAgent = async () => {
    setDownloading(true);
    try {
      const { token } = useAuthStore.getState();
      const res = await fetch(`/api/admin/agent-installer/${host.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const errText = await res.text().catch(() => 'Download failed');
        throw new Error(errText);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `csm-agent-${facilityId.toLowerCase()}-host${host.id}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Could not download agent: ${(err as Error).message}`);
    } finally {
      setDownloading(false);
    }
  };

  // Sheets claimed by OTHER hosts (for conflict detection)
  const claimedElsewhere = new Set(
    allHosts.filter(h => h.id !== host.id).flatMap(h => h.sessionMappings.map(m => m.sheetName))
  );

  const updateMutation = useMutation({
    mutationFn: () => agentHostApi.update(facilityId, host.id, {
      hostLabel: hostLabel.trim() || 'Host 1',
      obsWebSocketUrl: obsUrl.trim(),
      obsWebSocketPassword: obsPassword || null,
      pollIntervalSeconds: parseInt(pollInterval, 10) || 60,
      isActive,
      notes: notes || null,
      macAddress: macAddress.trim() || null,
      panelUrl: panelUrl.trim() || null,
      sessionMappings: mappings,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-hosts', facilityId] });
      setSaved(true); setSaveError(null);
      setTimeout(() => setSaved(false), 3000);
    },
    onError: (err: any) => setSaveError(err?.message || 'Failed to save'),
  });

  const regenMutation = useMutation({
    mutationFn: () => agentHostApi.regenerateKey(facilityId, host.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['agent-hosts', facilityId] }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => agentHostApi.remove(facilityId, host.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['agent-hosts', facilityId] }),
  });

  const handleCopyKey = () => {
    navigator.clipboard.writeText(host.apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Standard portable-OBS install path convention used across all facilities
  const defaultObsExePath = (sheetName: string): string =>
    sheetName ? `C:\\OBS\\SHEET ${sheetName}\\App\\obs-studio\\bin\\64bit\\obs64.exe` : '';

  const updateMapping = (index: number, field: keyof AgentSessionMapping, value: string | number | boolean) => {
    setMappings(prev => prev.map((m, i) => {
      if (i !== index) return m;
      const next = { ...m, [field]: value };
      // When the sheet name changes, auto-update the exe path IF the existing path matches the standard convention
      // (i.e. the user hasn't customized it). This avoids surprising users who've set a custom path.
      if (field === 'sheetName' && (!m.obsExePath || m.obsExePath === defaultObsExePath(m.sheetName))) {
        next.obsExePath = defaultObsExePath(String(value));
      }
      return next;
    }));
  };

  const addMapping = () => {
    // Find first unclaimed sheet name
    const used = new Set([...mappings.map(m => m.sheetName), ...claimedElsewhere]);
    const available = sheetNames.find(n => !used.has(n)) ?? '';
    setMappings(prev => [...prev, {
      sheetName: available,
      obsPort: 4455,
      streamKey: '',
      obsExePath: defaultObsExePath(available),
      obsEnabled: true,
    }]);
  };

  const removeMapping = (index: number) => {
    setMappings(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Card header */}
      <div className="flex items-center justify-between gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700">
        <input
          className="flex-1 min-w-0 text-sm font-semibold bg-transparent text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 rounded px-1 -ml-1"
          value={hostLabel}
          onChange={e => setHostLabel(e.target.value)}
          placeholder="Host label"
          title="Rename this host (e.g. 'Host 1', 'OBS East')"
        />
        <button
          onClick={() => {
            if (confirm(`Delete "${hostLabel}"? This cannot be undone.`)) deleteMutation.mutate();
          }}
          disabled={deleteMutation.isPending}
          className="shrink-0 text-xs text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-40 px-2 py-1"
          title="Delete this host"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="px-4 py-4 space-y-5">

        {/* API Key */}
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">API Key</p>
          <p className="mb-1.5 text-xs text-gray-500 dark:text-gray-400">
            Paste as <code>csmEndpoint.apiKey</code> in <code>host-agent.json</code> on this broadcast computer — or click <strong>Download Agent</strong> to get a pre-configured zip.
          </p>
          <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 dark:border-gray-700 dark:bg-gray-800/50">
            <code className="flex-1 truncate text-xs text-gray-700 dark:text-gray-300">
              {showApiKey ? host.apiKey : host.apiKeyMasked}
            </code>
            <button onClick={() => setShowApiKey(v => !v)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" title={showApiKey ? 'Hide' : 'Show'}>
              {showApiKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </button>
            <button onClick={handleCopyKey} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" title="Copy to clipboard">
              {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
            <Button
              size="sm" variant="outline"
              onClick={handleDownloadAgent}
              disabled={downloading}
              className="h-7 px-2 text-xs text-blue-600 hover:border-blue-300 dark:text-blue-400"
              title="Download a zip pre-configured with this host's API key"
            >
              {downloading ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Download className="mr-1 h-3 w-3" />}
              Download Agent
            </Button>
            <Button
              size="sm" variant="outline"
              onClick={() => { if (confirm('Regenerate the API key? The agent on this host will need to be updated.')) regenMutation.mutate(); }}
              disabled={regenMutation.isPending}
              className="h-7 px-2 text-xs text-orange-600 hover:border-orange-300 dark:text-orange-400"
            >
              <RefreshCw className="mr-1 h-3 w-3" />Regenerate
            </Button>
          </div>
        </div>

        {/* OBS settings */}
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className={labelCls}>OBS WebSocket Base URL</label>
            <input
              className={inputCls}
              value={obsUrl}
              onChange={e => setObsUrl(e.target.value)}
              placeholder="ws://192.168.254.20"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              IP (or hostname) of this broadcast computer. Port is set per-sheet below.
            </p>
          </div>
          <div>
            <label className={labelCls}>Shared Password</label>
            <div className="relative">
              <input
                className={inputCls + ' pr-8'}
                type={showPassword ? 'text' : 'password'}
                value={obsPassword}
                onChange={e => setObsPassword(e.target.value)}
                placeholder="Blank if auth disabled"
                autoComplete="off"
              />
              <button type="button" onClick={() => setShowPassword(v => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className={labelCls}>Poll Interval (seconds)</label>
            <input
              className={inputCls}
              type="number" min={10} max={3600}
              value={pollInterval}
              onChange={e => setPollInterval(e.target.value)}
            />
          </div>
          <div>
            <label className={labelCls}>MAC Address (Wake-on-LAN)</label>
            <input
              className={inputCls}
              value={macAddress}
              onChange={e => setMacAddress(e.target.value)}
              placeholder="AA:BB:CC:DD:EE:FF"
              autoComplete="off"
              spellCheck={false}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Optional. Used by external WoL tools or a peer host on the same LAN to wake this computer.
            </p>
          </div>
          <div>
            <label className={labelCls}>Panel URL (Control Hub)</label>
            <input
              className={inputCls}
              value={panelUrl}
              onChange={e => setPanelUrl(e.target.value)}
              placeholder="http://192.168.1.20:8090"
              autoComplete="off"
              spellCheck={false}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              LAN address of this host's control panel. Used by the onsite Control Hub / Dashboard to link and embed this host.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <input id={`host-active-${host.id}`} type="checkbox" checked={isActive}
            onChange={e => setIsActive(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-blue-600" />
          <label htmlFor={`host-active-${host.id}`} className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Host enabled
          </label>
        </div>

        {/* Per-sheet mappings */}
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Sheet Assignments
          </p>
          <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
            Map each sheet on this host to its OBS WebSocket port and YouTube persistent stream key.
            {claimedElsewhere.size > 0 && (
              <span className="ml-1 text-amber-600 dark:text-amber-400">
                Sheets already assigned to another host are shown with a warning.
              </span>
            )}
          </p>
          {mappings.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic mb-2">No sheets assigned to this host yet.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 mb-2">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400">Sheet</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400">OBS Port</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400">YouTube Stream Key</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400">OBS Exe Path</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-400" title="When enabled, the agent will auto-launch OBS on startup">Launch OBS</th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {mappings.map((m, i) => {
                    const conflict = claimedElsewhere.has(m.sheetName);
                    return (
                      <tr key={i} className={conflict ? 'bg-amber-50 dark:bg-amber-950/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800/40'}>
                        <td className="px-3 py-1.5">
                          <select
                            value={m.sheetName}
                            onChange={e => updateMapping(i, 'sheetName', e.target.value)}
                            title={conflict ? `"${m.sheetName}" is also assigned to another host` : undefined}
                            className={`rounded border px-2 py-1 text-sm bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 ${conflict ? 'border-amber-400 dark:border-amber-600' : 'border-gray-300 dark:border-gray-600'}`}
                          >
                            {sheetNames.map(n => (
                              <option key={n} value={n}
                                disabled={claimedElsewhere.has(n) && n !== m.sheetName}
                              >{n}{claimedElsewhere.has(n) && n !== m.sheetName ? ' (taken)' : ''}</option>
                            ))}
                            {/* Allow custom sheet name not in sheetNames list */}
                            {m.sheetName && !sheetNames.includes(m.sheetName) && (
                              <option value={m.sheetName}>{m.sheetName}</option>
                            )}
                          </select>
                        </td>
                        <td className="px-3 py-1.5">
                          <input
                            type="number" min={1024} max={65535}
                            value={m.obsPort}
                            onChange={e => updateMapping(i, 'obsPort', parseInt(e.target.value) || 4455)}
                            className="w-24 rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                          />
                        </td>
                        <td className="px-3 py-1.5">
                          <div className="flex items-center gap-1.5">
                            <input
                              type={showStreamKeys[i] ? 'text' : 'password'}
                              value={m.streamKey}
                              onChange={e => updateMapping(i, 'streamKey', e.target.value)}
                              placeholder="xxxx-xxxx-xxxx-xxxx-xxxx"
                              autoComplete="off"
                              className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                            />
                            <button type="button" onClick={() => setShowStreamKeys(prev => ({ ...prev, [i]: !prev[i] }))}
                              className="shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                              {showStreamKeys[i] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                            </button>
                          </div>
                        </td>
                        <td className="px-3 py-1.5">
                          <input
                            type="text"
                            value={m.obsExePath ?? ''}
                            onChange={e => updateMapping(i, 'obsExePath', e.target.value)}
                            placeholder="C:\OBS\SHEET A\App\obs-studio\bin\64bit\obs64.exe"
                            className="w-64 rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                          />
                        </td>
                        <td className="px-3 py-1.5 text-center">
                          <input
                            type="checkbox"
                            checked={m.obsEnabled !== false}
                            onChange={e => updateMapping(i, 'obsEnabled', e.target.checked)}
                            title="Auto-launch OBS on agent startup"
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-2">
                          <button onClick={() => removeMapping(i)} className="text-gray-400 hover:text-red-500" title="Remove row">
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <button
            onClick={addMapping}
            className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
          >
            + Add sheet
          </button>
        </div>

        {/* Notes */}
        <div>
          <label className={labelCls}>Notes</label>
          <textarea
            className={inputCls} rows={2}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Optional notes about this host…"
          />
        </div>

        <SaveBar onSave={() => updateMutation.mutate()} isPending={updateMutation.isPending} saved={saved} error={saveError} />
      </div>
    </div>
  );
}

// ─── Agent Tab ────────────────────────────────────────────────────────────────
// Standard OBS streaming profile (global) — view + replace. The agent applies it
// per-sheet (with each sheet's stream key injected) in a later step.
function ObsProfileCard({ facilityId, facilityName }: { facilityId: string; facilityName: string }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [files, setFiles] = useState<{ basicIni?: string; serviceJson?: string; streamEncoderJson?: string }>({});
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [applyMsg, setApplyMsg] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['obs-template'],
    queryFn: () => obsTemplateApi.get(),
  });

  const applyMutation = useMutation({
    mutationFn: () => obsTemplateApi.apply(facilityId),
    onSuccess: (r) => {
      setApplyMsg(`Requested for ${r.targeted} sheet(s) — each OBS instance applies the profile when that sheet is idle (it restarts OBS).`);
      setTimeout(() => setApplyMsg(null), 12000);
    },
    onError: (e: any) => setApplyMsg(`Apply failed: ${e?.message || 'error'}`),
  });

  const handleApply = () => {
    const msg = `Apply the standard OBS profile to ALL of ${facilityName}'s sheets?\n\nEach sheet's OBS will switch to "${data?.template.profileName ?? 'CSM Agent-Auto'}" with its stream key injected — which RESTARTS OBS on that sheet when it's idle. Sheets that are live are skipped until they finish. Continue?`;
    if (!window.confirm(msg)) return;
    applyMutation.mutate();
  };

  const updateMutation = useMutation({
    mutationFn: () => obsTemplateApi.update({
      basicIni: files.basicIni!, serviceJson: files.serviceJson!, streamEncoderJson: files.streamEncoderJson!,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['obs-template'] });
      setSaved(true); setEditing(false); setFiles({}); setErr(null);
      setTimeout(() => setSaved(false), 3000);
    },
    onError: (e: any) => setErr(e?.message || 'Failed to save template'),
  });

  const readFile = (key: keyof typeof files) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setFiles(prev => ({ ...prev, [key]: String(reader.result) }));
    reader.readAsText(f);
  };

  const s = data?.summary;
  const allThree = !!(files.basicIni && files.serviceJson && files.streamEncoderJson);

  return (
    <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">Standard OBS Profile</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Shared across all facilities. Applied per sheet with that sheet's stream key injected.</p>
        </div>
        {!editing && (
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handleApply} disabled={applyMutation.isPending || isLoading}>
              {applyMutation.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
              Apply to {facilityName}
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setEditing(true); setErr(null); }}>Replace…</Button>
          </div>
        )}
      </div>
      {applyMsg && <p className="mt-2 text-xs text-blue-600 dark:text-blue-400">{applyMsg}</p>}

      {isLoading ? (
        <Loader2 className="mt-3 h-4 w-4 animate-spin text-gray-400" />
      ) : (
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-600 dark:text-gray-300">
          <span><span className="text-gray-400">Resolution:</span> {s?.resolution ?? '—'}</span>
          <span><span className="text-gray-400">FPS:</span> {s?.fps ?? '—'}</span>
          <span><span className="text-gray-400">Encoder:</span> {s?.encoder ?? '—'}</span>
          <span><span className="text-gray-400">Rate:</span> {s?.rateControl ?? '—'}</span>
          <span><span className="text-gray-400">Bitrate:</span> {s?.bitrateKbps ? `${s.bitrateKbps} kbps` : '—'}</span>
          <span><span className="text-gray-400">v</span>{data?.template.version}</span>
          {saved && <span className="text-green-600 dark:text-green-400">Saved ✓</span>}
        </div>
      )}

      {editing && (
        <div className="mt-4 space-y-2 border-t border-gray-200 pt-3 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400">Upload the three files from an OBS profile export. The stream key is stripped automatically.</p>
          {(['basicIni', 'serviceJson', 'streamEncoderJson'] as const).map(k => (
            <div key={k} className="flex items-center gap-2 text-xs">
              <span className="w-32 text-gray-500 dark:text-gray-400">
                {k === 'basicIni' ? 'basic.ini' : k === 'serviceJson' ? 'service.json' : 'streamEncoder.json'}
              </span>
              <input type="file" onChange={readFile(k)} className="text-xs text-gray-600 dark:text-gray-300" />
              {files[k] && <Check className="h-3.5 w-3.5 text-green-500" />}
            </div>
          ))}
          {err && <p className="text-xs text-red-500">{err}</p>}
          <div className="flex gap-2 pt-1">
            <Button size="sm" disabled={!allThree || updateMutation.isPending} onClick={() => updateMutation.mutate()}>
              {updateMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save template'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setEditing(false); setFiles({}); setErr(null); }}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Control Hub config (facility-level) ─────────────────────────────────────
// Onsite operators get an optional "Dashboard" + "Control Hub" page on the agent
// (:8090). Host panel tabs are auto-added from each host's Panel URL above; here we
// set the master enable switch, the Companion web-buttons URL, and any custom tabs.
function ControlHubCard({ facility, hosts }: { facility: any; hosts: AgentHost[] }) {
  const queryClient = useQueryClient();
  const initial: ControlHubConfig = facility.controlHubConfig ?? { enabled: false, companionEmbed: 'iframe', customTabs: [] };
  const [enabled, setEnabled]           = useState<boolean>(initial.enabled ?? false);
  const [companionUrl, setCompanionUrl] = useState<string>(initial.companionUrl ?? '');
  const [companionEmbed, setCompanionEmbed] = useState<'iframe' | 'link'>(initial.companionEmbed ?? 'iframe');
  const [customTabs, setCustomTabs]     = useState<ControlHubTab[]>(initial.customTabs ?? []);
  const [saved, setSaved]   = useState(false);
  const [error, setError]   = useState<string | null>(null);

  const hostsWithPanel = hosts.filter(h => h.panelUrl);

  const mutation = useMutation({
    mutationFn: () => {
      const cfg: ControlHubConfig = {
        enabled,
        companionUrl: companionUrl.trim() || null,
        companionEmbed,
        customTabs: customTabs
          .filter(t => t.label.trim() && t.url.trim())
          .map(t => ({ id: t.id, label: t.label.trim(), url: t.url.trim(), embed: t.embed })),
      };
      return facilitiesApi.update(facility.id, { controlHubConfig: cfg });
    },
    onSuccess: () => {
      // The settings page loads the facility via facilities.detail(id) — invalidate
      // that (not just .all) so the saved Control Hub config is re-read into the form.
      queryClient.invalidateQueries({ queryKey: queryKeys.facilities.detail(facility.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.facilities.all });
      setSaved(true); setError(null);
      setTimeout(() => setSaved(false), 3000);
    },
    onError: (err: any) => setError(err?.message || 'Failed to save'),
  });

  const addTab = () => setCustomTabs(t => [...t, { id: crypto.randomUUID(), label: '', url: '', embed: 'iframe' }]);
  const updateTab = (id: string, patch: Partial<ControlHubTab>) =>
    setCustomTabs(t => t.map(x => x.id === id ? { ...x, ...patch } : x));
  const removeTab = (id: string) => setCustomTabs(t => t.filter(x => x.id !== id));

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Onsite Control Hub &amp; Dashboard</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Optional one-page view on each host's control panel that embeds the other hosts, Companion, and custom tools.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-blue-600" />
          Enabled
        </label>
      </div>

      {enabled && (
        <>
          <div className="rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:bg-gray-800/50 dark:text-gray-400">
            {hostsWithPanel.length > 0
              ? <>Host panel tabs (auto-added): {hostsWithPanel.map(h => h.hostLabel).join(', ')}. Set each host's <strong>Panel URL</strong> above to include it.</>
              : <>No host Panel URLs set yet — add a <strong>Panel URL</strong> to each host above so they appear as tabs.</>}
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div className="sm:col-span-2">
              <label className={labelCls}>Companion Web-Buttons URL</label>
              <input className={inputCls} value={companionUrl} onChange={e => setCompanionUrl(e.target.value)}
                placeholder="http://192.168.1.50:8000/tablet" autoComplete="off" spellCheck={false} />
            </div>
            <div>
              <label className={labelCls}>Companion embed</label>
              <select className={inputCls} value={companionEmbed} onChange={e => setCompanionEmbed(e.target.value as 'iframe' | 'link')}>
                <option value="iframe">Embed (iframe)</option>
                <option value="link">Link only</option>
              </select>
            </div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            If Companion refuses to embed (blank tab), switch it to <strong>Link only</strong>. Targets must be reachable from the operator's browser on the LAN (http).
          </p>

          {/* Custom tabs */}
          <div className="space-y-2">
            <label className={labelCls}>Custom tabs (OBS Web, etc.)</label>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              The <strong>Tab Label</strong> is the tab name shown in the onsite hub. (Host and Companion tabs are named automatically.)
            </p>
            {customTabs.length === 0 && (
              <p className="text-xs text-gray-400 dark:text-gray-500">No custom tabs.</p>
            )}
            {customTabs.map(tab => (
              <div key={tab.id} className="grid grid-cols-1 gap-2 sm:grid-cols-12">
                <input className={`${inputCls} sm:col-span-3`} value={tab.label}
                  onChange={e => updateTab(tab.id, { label: e.target.value })} placeholder="Tab Label" />
                <input className={`${inputCls} sm:col-span-6`} value={tab.url}
                  onChange={e => updateTab(tab.id, { url: e.target.value })} placeholder="http://..." spellCheck={false} />
                <select className={`${inputCls} sm:col-span-2`} value={tab.embed}
                  onChange={e => updateTab(tab.id, { embed: e.target.value as 'iframe' | 'link' })}>
                  <option value="iframe">Embed</option>
                  <option value="link">Link</option>
                </select>
                <button onClick={() => removeTab(tab.id)}
                  className="rounded px-2 py-1 text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 sm:col-span-1">
                  Remove
                </button>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={addTab}>+ Add tab</Button>
          </div>
        </>
      )}

      {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
      <div className="flex items-center gap-3">
        <Button size="sm" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
          {mutation.isPending ? 'Saving…' : 'Save Control Hub'}
        </Button>
        {saved && <span className="text-xs text-green-600 dark:text-green-400">Saved</span>}
      </div>
    </div>
  );
}

function AgentTab({ facility }: { facility: any }) {
  const queryClient = useQueryClient();
  const facilityId = facility.facilityId as string;

  const sheetNames: string[] = (() => {
    try { return JSON.parse(facility.sheetNames ?? '[]'); } catch { return []; }
  })();

  const { data, isLoading } = useQuery({
    queryKey: ['agent-hosts', facilityId],
    queryFn: () => agentHostApi.list(facilityId),
  });

  const hosts = data?.hosts ?? [];

  const createMutation = useMutation({
    mutationFn: () => agentHostApi.create(facilityId, {
      hostLabel: `Host ${hosts.length + 1}`,
      obsWebSocketUrl: 'ws://localhost',
      obsWebSocketPassword: null,
      pollIntervalSeconds: 60,
      isActive: true,
      notes: null,
      sessionMappings: [],
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['agent-hosts', facilityId] }),
  });

  if (isLoading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>;
  }

  return (
    <div className="space-y-6">

      {/* Setup callout */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm dark:border-blue-800/50 dark:bg-blue-950/30">
        <p className="font-semibold text-blue-800 dark:text-blue-300 mb-1.5">How the CSM Agent works</p>
        <ol className="list-decimal list-inside space-y-1 text-blue-700 dark:text-blue-400 text-xs leading-relaxed">
          <li>Install the CSM Agent on the broadcast computer. For facilities with multiple OBS hosts, add a Host card for each computer below.</li>
          <li>Copy the <strong>API Key</strong> from the host card and paste it as <code>csmEndpoint.apiKey</code> in <code>host-agent.json</code>.</li>
          <li>Set the <strong>OBS WebSocket base URL</strong> to the IP address of that broadcast computer (e.g. <code>ws://192.168.254.20</code>).</li>
          <li>Assign sheets to each host — each sheet's OBS port and YouTube stream key go in the Sheet Assignments table.</li>
          <li>Start the agent — it polls CSM and auto-starts / stops streams according to the schedule.</li>
        </ol>
      </div>

      {/* Standard OBS profile */}
      <ObsProfileCard facilityId={facilityId} facilityName={facility.name} />

      {/* Host cards */}
      {hosts.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 italic">
          No agent hosts configured. Click "Add Host" to set one up.
        </p>
      ) : (
        hosts.map(host => (
          <HostCard
            key={host.id}
            host={host}
            facilityId={facilityId}
            sheetNames={sheetNames}
            allHosts={hosts}
          />
        ))
      )}

      {/* Add host button */}
      <Button
        variant="outline"
        onClick={() => createMutation.mutate()}
        disabled={createMutation.isPending}
        className="w-full border-dashed text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
      >
        {createMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        + Add Host
      </Button>

      {/* Onsite Control Hub / Dashboard config */}
      <ControlHubCard facility={facility} hosts={hosts} />
    </div>
  );
}

// ─── Setup Tab ──────────────────────────────────────────────────────────────────
// Status-driven onboarding checklist. Each step shows a live badge + an inline
// action. Status comes from GET /facilities/:id/setup-status (polled); actions
// reuse the existing endpoints (YouTube connect/create-keys, sync-keys, apply).
function SetupStatusBadge({ status }: { status: SetupStep['status'] }) {
  if (status === 'done') {
    return (
      <span className="flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400">
        <CheckCircle2 className="h-4 w-4" /> Done
      </span>
    );
  }
  if (status === 'blocked') {
    return (
      <span className="flex items-center gap-1 text-xs font-medium text-gray-400 dark:text-gray-500">
        <Lock className="h-4 w-4" /> Blocked
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-xs font-medium text-amber-600 dark:text-amber-400">
      <Circle className="h-4 w-4" /> To do
    </span>
  );
}

function SetupTab({ facility, onNavigateTab }: { facility: any; onNavigateTab: (t: Tab) => void }) {
  const queryClient = useQueryClient();
  const facilityId = facility.facilityId as string;
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: queryKeys.setup.status(facilityId),
    queryFn: () => setupApi.getStatus(facilityId),
    // Poll so host-online / applied-version steps reflect live agent heartbeats.
    refetchInterval: 15000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.setup.status(facilityId) });
  const flash = (m: string) => { setMsg(m); setErr(null); setTimeout(() => setMsg(null), 8000); };
  const flashErr = (m: string) => { setErr(m); setMsg(null); };

  const createKeysMutation = useMutation({
    mutationFn: () => youtubeApi.createStreamKeys(facilityId),
    onSuccess: (r) => { flash(`Stream keys: created ${r.created}, found ${r.found}${r.missing ? `, ${r.missing} still missing` : ''}.`); invalidate(); },
    onError: (e: any) => flashErr(e?.message || 'Failed to create stream keys'),
  });

  const syncMutation = useMutation({
    mutationFn: () => obsTemplateApi.syncSessionKeys(facilityId),
    onSuccess: (r) => {
      flash(`Synced keys to host config: filled ${r.filled}, overwritten ${r.overwritten}, created ${r.created}, unchanged ${r.same} (${r.hostsUpdated} host(s) updated).${r.flags.length ? ` ${r.flags.length} need manual attention.` : ''}`);
      if (r.flags.length) setErr(r.flags.join(' '));
      invalidate();
    },
    onError: (e: any) => flashErr(e?.message || 'Failed to sync keys'),
  });

  const applyMutation = useMutation({
    mutationFn: () => obsTemplateApi.apply(facilityId),
    onSuccess: (r) => { flash(`OBS profile requested for ${r.targeted} sheet(s) — each instance applies when idle (restarts OBS). Status confirms once agents report back.`); invalidate(); },
    onError: (e: any) => flashErr(e?.message || 'Failed to apply OBS profile'),
  });

  const handleConnectYouTube = () => {
    const authUrl = `/api/youtube/oauth/authorize?facilityId=${facilityId}`;
    const popup = window.open(authUrl, 'youtube-auth', 'width=600,height=700,scrollbars=yes,resizable=yes');
    const listener = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'youtube-oauth-success') {
        window.removeEventListener('message', listener);
        popup?.close();
        queryClient.invalidateQueries({ queryKey: queryKeys.youtube.credentials(facilityId) });
        invalidate();
      }
    };
    window.addEventListener('message', listener);
    const interval = setInterval(() => {
      if (popup?.closed) { clearInterval(interval); window.removeEventListener('message', listener); invalidate(); }
    }, 1000);
  };

  const handleSync = () => {
    if (!window.confirm(`Sync recorded stream keys into ${facility.name}'s host config?\n\nThis fills/overwrites each sheet's streamKey in the agent host's session mappings to match the YouTube registry. Idempotent and safe to re-run.`)) return;
    syncMutation.mutate();
  };

  const handleApply = () => {
    if (!window.confirm(`Apply the standard OBS profile to ALL of ${facility.name}'s sheets?\n\nEach sheet's OBS switches to the standard profile with its stream key injected — which RESTARTS OBS on that sheet when it's idle. Continue?`)) return;
    applyMutation.mutate();
  };

  // Inline action element per step key.
  const actionFor = (step: SetupStep): React.ReactNode => {
    const pending = createKeysMutation.isPending || syncMutation.isPending || applyMutation.isPending;
    switch (step.key) {
      case 'sheets':
        return <Button size="sm" variant="outline" onClick={() => onNavigateTab('sheets')}>Edit sheets</Button>;
      case 'youtube':
        return step.status === 'done'
          ? <Button size="sm" variant="outline" onClick={() => onNavigateTab('youtube')}>Manage</Button>
          : <Button size="sm" onClick={handleConnectYouTube}>Connect YouTube</Button>;
      case 'streamKeys':
        return (
          <Button size="sm" disabled={step.status === 'blocked' || pending}
            variant={step.status === 'done' ? 'outline' : 'default'}
            onClick={() => createKeysMutation.mutate()}>
            {createKeysMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : (step.status === 'done' ? 'Re-check' : 'Create keys')}
          </Button>
        );
      case 'keysInConfig':
        return (
          <Button size="sm" disabled={step.status === 'blocked' || pending}
            variant={step.status === 'done' ? 'outline' : 'default'}
            onClick={handleSync}>
            {syncMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : (step.status === 'done' ? 'Re-sync' : 'Sync to host config')}
          </Button>
        );
      case 'agentHost':
        return <Button size="sm" variant="outline" onClick={() => onNavigateTab('agent')}>Manage hosts</Button>;
      case 'obsProfile':
        return (
          <Button size="sm" disabled={step.status === 'blocked' || pending}
            variant={step.status === 'done' ? 'outline' : 'default'}
            onClick={handleApply}>
            {applyMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : (step.status === 'done' ? 'Re-apply' : 'Apply profile')}
          </Button>
        );
      case 'schedules':
        return <Button size="sm" variant="outline" onClick={() => onNavigateTab('schedule')}>Edit schedule</Button>;
      default:
        return null;
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>;
  }
  if (!data) {
    return <div className="text-sm text-red-600 dark:text-red-400">Failed to load setup status.</div>;
  }

  return (
    <div className="space-y-4">
      {/* Header + progress */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Onboarding</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">{data.completed} of {data.total} steps complete</p>
        </div>
        <Button size="sm" variant="ghost" onClick={() => refetch()} title="Refresh status">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
        <div className="h-full rounded-full bg-green-500 transition-all"
          style={{ width: `${data.total ? (data.completed / data.total) * 100 : 0}%` }} />
      </div>

      {msg && (
        <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-300">
          {msg}
        </div>
      )}
      {err && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
          {err}
        </div>
      )}

      {/* Step rows */}
      <ol className="space-y-2">
        {data.steps.map((step, i) => (
          <li key={step.key}
            className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800/40">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600 dark:bg-gray-700 dark:text-gray-300">
              {i + 1}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{step.label}</span>
                <SetupStatusBadge status={step.status} />
              </div>
              <p className="truncate text-xs text-gray-500 dark:text-gray-400">{step.detail}</p>
            </div>
            <div className="shrink-0">{actionFor(step)}</div>
          </li>
        ))}
      </ol>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function FacilitySettingsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<Tab>('setup');

  const { data: facility, isLoading, error } = useQuery({
    queryKey: queryKeys.facilities.detail(Number(id)),
    queryFn: () => facilitiesApi.getById(Number(id)),
    enabled: !!id,
  });

  const handleSaved = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.facilities.all });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !facility) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          <AlertCircle className="mb-1 inline h-4 w-4" /> Failed to load facility.
        </div>
      </div>
    );
  }

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'setup',    label: 'Setup',    icon: <ListChecks className="h-4 w-4" /> },
    { id: 'general',  label: 'General',  icon: <Building2 className="h-4 w-4" /> },
    { id: 'sheets',   label: 'Sheets',   icon: <LayoutGrid className="h-4 w-4" /> },
    { id: 'schedule', label: 'Schedule', icon: <Calendar className="h-4 w-4" /> },
    { id: 'youtube',  label: 'YouTube',  icon: <Youtube className="h-4 w-4" /> },
    { id: 'agent',    label: 'Agent',    icon: <Cpu className="h-4 w-4" /> },
    { id: 'cameras',  label: 'Cameras',  icon: <Camera className="h-4 w-4" /> },
    { id: 'alerts',   label: 'Alerts',   icon: <Bell className="h-4 w-4" /> },
  ];

  return (
    <div className="container mx-auto max-w-3xl px-4 py-8">
      {/* Back link */}
      <button
        onClick={() => navigate('/facilities')}
        className="mb-6 flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Facilities
      </button>

      {/* Header */}
      <div className="mb-6 flex items-start gap-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
          <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{facility.name}</h1>
          <div className="mt-1 flex items-center gap-2">
            <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs dark:bg-gray-800">
              {facility.facilityId}
            </code>
            <Badge variant={facility.isActive ? 'default' : 'secondary'}>
              {facility.isActive ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="mb-6 flex flex-wrap gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1 dark:border-gray-700 dark:bg-gray-800/50">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-gray-100'
                : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <Card>
        <CardContent className="pt-6">
          {activeTab === 'setup' && (
            <SetupTab facility={facility} onNavigateTab={setActiveTab} />
          )}
          {activeTab === 'general' && (
            <GeneralTab facility={facility} onSaved={handleSaved} />
          )}
          {activeTab === 'sheets' && (
            <SheetsTab facility={facility} onSaved={handleSaved} />
          )}
          {activeTab === 'schedule' && (
            <ScheduleTab facility={facility} onSaved={handleSaved} />
          )}
          {activeTab === 'youtube' && (
            <YouTubeTab facility={facility} />
          )}
          {activeTab === 'agent' && (
            <AgentTab facility={facility} />
          )}
          {activeTab === 'cameras' && (
            <CameraInventoryTab facility={facility} />
          )}
          {activeTab === 'alerts' && (
            <AlertsTab facility={facility} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
